import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./About.css";

/* ── Intersection Observer hook ── */
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

/* ── Animated counter ── */
function Counter({ target, suffix = "" }) {
  const [count, setCount] = useState(0);
  const [ref, visible] = useReveal();
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const end = parseInt(target);
    const step = Math.ceil(end / (1800 / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [visible, target]);
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

/* ── Scroll reveal wrapper ── */
function Reveal({ children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`ab-reveal ${visible ? "ab-reveal--in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ══════════ DATA ══════════ */

const STATS = [
  { n: "9",    suffix: "M+",  label: "Professionals Served", icon: "👥" },
  { n: "500",  suffix: "K+",  label: "Jobs Posted Daily",    icon: "💼" },
  { n: "50",   suffix: "K+",  label: "Hiring Partners",      icon: "🏢" },
  { n: "150",  suffix: "+",   label: "Cities Covered",       icon: "📍" },
];

const VALUES = [
  {
    icon: "🎯",
    title: "Precision Matching",
    desc: "Our AI doesn't spray your profile across hundreds of irrelevant listings. It finds roles where you'll genuinely thrive — matching skills, culture, and growth potential.",
  },
  {
    icon: "🔍",
    title: "Radical Transparency",
    desc: "Salary ranges are shown upfront. Every employer is verified. No hidden processes, no ghosting — just clear, honest hiring from start to offer letter.",
  },
  {
    icon: "🚀",
    title: "Career Acceleration",
    desc: "Beyond job listings, we arm you with Resume Builder, ATS Checker, Salary Guides, and 7 financial calculators — because landing the job is just the beginning.",
  },
  {
    icon: "🇮🇳",
    title: "Deeply Indian",
    desc: "Built for India's 500 million-strong workforce — from metro professionals to Tier 3 city talent, from freshers to CXOs, across every sector and language.",
  },
  {
    icon: "⚡",
    title: "Instant & Effortless",
    desc: "One-tap applications. Real-time alerts. Zero friction. We respect that your time is valuable — so we cut everything that doesn't move your career forward.",
  },
  {
    icon: "❤️",
    title: "Human at the Core",
    desc: "Technology powers us, but people define us. Every feature we build is measured by one question: does this make someone's professional life meaningfully better?",
  },
];

const TEAM = [
  { name: "Arjun Sharma",   role: "Chief Executive Officer",         avatar: "AS", color: "#263012" },
  { name: "Priya Menon",    role: "Chief Technology Officer",        avatar: "PM", color: "#0891b2" },
  { name: "Rohit Kapoor",   role: "VP — Product & Design",           avatar: "RK", color: "#394420" },
  { name: "Sneha Iyer",     role: "VP — Employer Partnerships",      avatar: "SI", color: "#7c3aed" },
  { name: "Dev Malhotra",   role: "Engineering Lead",                avatar: "DM", color: "#db2777" },
  { name: "Ananya Singh",   role: "VP — Growth & Marketing",         avatar: "AN", color: "#263012" },
];

const MILESTONES = [
  {
    year: "2019",
    title: "TalentHive Launched",
    desc: "With a clear vision and a passionate founding team, TalentHive went live — setting out to solve India's most frustrating career challenge.",
  },
  {
    year: "2020",
    title: "Crossed 1 Million Users",
    desc: "Word spread fast. Within 14 months, over a million professionals had made TalentHive their primary career platform.",
  },
  {
    year: "2021",
    title: "AI Matching Engine v1.0",
    desc: "We launched our proprietary matching algorithm — delivering job recommendations with 94% relevance accuracy, setting a new industry benchmark.",
  },
  {
    year: "2022",
    title: "Series B Funding — ₹400 Cr",
    desc: "Backed by top-tier investors, we accelerated our expansion into Tier 2 and Tier 3 cities, bringing quality opportunities to every corner of India.",
  },
  {
    year: "2023",
    title: "Career Tools Suite",
    desc: "Launched 9 free career tools including Resume Builder, ATS Checker, Salary Calculator, and Retirement Planner — used by over 2M professionals.",
  },
  {
    year: "2024",
    title: "9 Million+ Professionals",
    desc: "TalentHive became India's most-loved job platform, with 50,000+ verified employers and ₹2,400 Cr+ in annual salary outcomes for our users.",
  },
];

const AWARDS = [
  { icon: "🏆", title: "Best Job Portal 2024",      org: "India Startup Awards"     },
  { icon: "⭐", title: "Top HR Tech Innovator",      org: "Economic Times Conclave"  },
  { icon: "🎖️", title: "Most Trusted Platform",     org: "LinkedIn Top Startups 50" },
  { icon: "🌟", title: "Best UX in Recruitment",     org: "Product Hunt Golden Kitty" },
];

const PRESS = [
  { name: "Economic Times",  quote: "The platform redefining how India hires." },
  { name: "YourStory",       quote: "India's most ambitious career super-app." },
  { name: "Inc42",           quote: "Solving hiring at a scale no one else has." },
  { name: "Business Today",  quote: "A catalyst for India's talent revolution." },
];

/* ══════════ COMPONENT ══════════ */
export default function AboutUs() {
  return (
    <div className="ab-root">


      {/* ══ STORY ══ */}
      <section className="ab-section ab-story" data-aos="fade-up">
        <div className="ab-story-text">
          <Reveal>
            <span className="ab-eyebrow">Our Story</span>
            <h2 className="ab-h2">Built to solve India's<br />biggest career challenge</h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="ab-body">
              India has always had extraordinary talent. What it lacked was a platform worthy of that talent — one that understood the nuances of the Indian job market, spoke to professionals at every level, and treated the job search as the life-defining event it truly is.
            </p>
          </Reveal>
          <Reveal delay={180}>
            <p className="ab-body">
              TalentHive was built to fill that gap. We combined deep HR domain expertise with cutting-edge AI to create a platform that doesn't just list jobs — it understands careers. Our recommendations are personalised. Our tools are genuinely useful. Our employer relationships are built on accountability.
            </p>
          </Reveal>
          <Reveal delay={260}>
            <p className="ab-body">
              Today, with 9 million professionals and 50,000+ companies on board, TalentHive processes more meaningful career connections per day than any other platform in India. But we measure success differently — in salaries raised, families uplifted, and potential unlocked.
            </p>
          </Reveal>
          <Reveal delay={320}>
            <div className="ab-story-metrics">
              {[
                { n: "₹2,400 Cr+", l: "Annual salaries facilitated" },
                { n: "3.2 Lakhs",  l: "Hires in 2024 alone"         },
                { n: "11 Min",     l: "Avg. time to first application"},
              ].map(({ n, l }) => (
                <div key={l} className="ab-sm">
                  <span className="ab-sm-n">{n}</span>
                  <span className="ab-sm-l">{l}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="ab-story-visual">
          <Reveal delay={150}>
            <div className="ab-story-card">
              <div className="ab-story-card-inner">
                <div className="ab-story-quote">"</div>
                <p className="ab-story-qtext">
                  The best career platforms don't help people find jobs — they help people find themselves professionally. That's the standard TalentHive holds itself to, every single day.
                </p>
                <div className="ab-story-author">
                  <div className="ab-story-avatar" style={{ background: "#263012" }}>AS</div>
                  <div>
                    <div className="ab-story-aname">Arjun Sharma</div>
                    <div className="ab-story-arole">CEO, TalentHive</div>
                  </div>
                </div>
              </div>
              <div className="ab-story-blob" />
            </div>
          </Reveal>

          <Reveal delay={250}>
            <div className="ab-story-card ab-story-card--sm">
              <div className="ab-story-card-inner">
                <div className="ab-story-quote" style={{ fontSize: 48 }}>"</div>
                <p className="ab-story-qtext" style={{ fontSize: 14 }}>
                  We don't compete on quantity of listings. We compete on quality of outcomes. Every feature we ship has one question behind it: does this help a real person get a better job?
                </p>
                <div className="ab-story-author">
                  <div className="ab-story-avatar" style={{ background: "#0891b2" }}>PM</div>
                  <div>
                    <div className="ab-story-aname">Priya Menon</div>
                    <div className="ab-story-arole">CTO, TalentHive</div>
                  </div>
                </div>
              </div>
              <div className="ab-story-blob ab-story-blob--blue" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ PRESS ══ */}
      <div className="ab-press" data-aos="fade-up">
        <span className="ab-press-label">As seen in</span>
        <div className="ab-press-logos">
          {PRESS.map(({ name, quote }) => (
            <div key={name} className="ab-press-item">
              <div className="ab-press-name">{name}</div>
              <div className="ab-press-quote">"{quote}"</div>
            </div>
          ))}
        </div>
      </div>

      {/* ══ VALUES ══ */}
      <section className="ab-values-section" data-aos="fade-up">
        <div className="ab-values-inner">
          <Reveal className="ab-values-head">
            <span className="ab-eyebrow">What We Stand For</span>
            <h2 className="ab-h2 ab-h2--center">The principles that drive us</h2>
            <p className="ab-section-sub">
              Six commitments we make to every professional and every employer who places their trust in us.
            </p>
          </Reveal>
          <div className="ab-values-grid">
            {VALUES.map(({ icon, title, desc }, i) => (
              <Reveal key={title} delay={i * 75}>
                <div className="ab-value-card">
                  <div className="ab-value-icon-wrap">
                    <span className="ab-value-icon">{icon}</span>
                  </div>
                  <h3 className="ab-value-title">{title}</h3>
                  <p className="ab-value-desc">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ TIMELINE ══ */}
      <section className="ab-section ab-tl-section" data-aos="fade-up">
        <Reveal className="ab-tl-head">
          <span className="ab-eyebrow">Our Journey</span>
          <h2 className="ab-h2 ab-h2--center">Five years that changed Indian hiring</h2>
          <p className="ab-section-sub">
            From a bold idea to a platform that touches millions of careers every single day.
          </p>
        </Reveal>
        <div className="ab-tl">
          <div className="ab-tl-line" />
          {MILESTONES.map(({ year, title, desc }, i) => (
            <Reveal key={year} delay={i * 90}>
              <div className={`ab-tl-row ${i % 2 === 0 ? "ab-tl-row--l" : "ab-tl-row--r"}`}>
                <div className="ab-tl-card">
                  <span className="ab-tl-year">{year}</span>
                  <h3 className="ab-tl-title">{title}</h3>
                  <p className="ab-tl-desc">{desc}</p>
                </div>
                <div className="ab-tl-dot">
                  <div className="ab-tl-dot-inner" />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ══ TEAM ══ */}
      <section className="ab-team-section" data-aos="fade-up">
        <div className="ab-team-inner">
          <Reveal className="ab-team-head">
            <span className="ab-eyebrow">The People</span>
            <h2 className="ab-h2 ab-h2--center">Leadership that's been there, hired that</h2>
            <p className="ab-section-sub">
              Our leadership team brings decades of combined experience across technology, HR, and consumer products.
            </p>
          </Reveal>
          <div className="ab-team-grid">
            {TEAM.map(({ name, role, avatar, color }, i) => (
              <Reveal key={name} delay={i * 75}>
                <div className="ab-team-card">
                  <div className="ab-team-avatar" style={{ background: color }}>{avatar}</div>
                  <div className="ab-team-name">{name}</div>
                  <div className="ab-team-role">{role}</div>
                  <a href="#" className="ab-team-link">LinkedIn ↗</a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ AWARDS ══ */}
      <section className="ab-section ab-awards-section" data-aos="fade-up">
        <Reveal className="ab-awards-head">
          <span className="ab-eyebrow">Recognition</span>
          <h2 className="ab-h2 ab-h2--center">Industry recognition</h2>
          <p className="ab-section-sub">
            Independent recognition from India's most respected media and analyst organisations.
          </p>
        </Reveal>
        <div className="ab-awards-grid">
          {AWARDS.map(({ icon, title, org }, i) => (
            <Reveal key={title} delay={i * 80}>
              <div className="ab-award-card">
                <span className="ab-award-icon">{icon}</span>
                <div className="ab-award-title">{title}</div>
                <div className="ab-award-org">{org}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section className="ab-cta" data-aos="fade-up">
        <div className="ab-cta-bg">
          <div className="ab-cta-orb ab-cta-orb--1" />
          <div className="ab-cta-orb ab-cta-orb--2" />
          <div className="ab-cta-grid" />
        </div>
        <Reveal className="ab-cta-inner">
          <span className="ab-eyebrow ab-eyebrow--dark">Join 9M+ Professionals</span>
          <h2 className="ab-cta-h2">
            Your next opportunity<br />is waiting for you
          </h2>
          <p className="ab-cta-sub">
            Whether you're actively looking or passively open — TalentHive
            puts the right opportunities in front of you, at the right time.
          </p>
          <div className="ab-cta-actions">
            <Link to="/employee/jobs"           className="ab-cta-btn-primary">Explore Jobs →</Link>
            <Link to="/employee/resume-builder" className="ab-cta-btn-outline">Build Your Resume</Link>
          </div>
          <div className="ab-cta-trust">
            {["Free to join", "No spam", "Cancel anytime"].map(t => (
              <span key={t} className="ab-cta-trust-item">
                <span className="ab-cta-check">✓</span> {t}
              </span>
            ))}
          </div>
        </Reveal>
      </section>

    </div>
  );
}
